import React, { useState, useEffect } from 'react';

// Function → carton source mapping (same as MEESA)
const FN_CARTON_MAP = {
  'Vendor Line Load': 'pid', 'PID Manual Divert': 'pid', 'ReceiveParcel Unload': 'pid', 'Receive Dock Crew': 'pid', 'PID Box Opener': 'pid',
  'ReceiveLine Unloader': 'aros', 'PID Sort Line': 'aros',
  'Vendor Sort': 'pr', 'IB Robotics Operator': 'pr', 'PID Truck Unload': 'pr',
  'Inbound Dock Clerk': 'ibCartons', 'Receive Dock TDR': 'ibCartons', 'Pallet Unload': 'ibCartons', 'FreightID': 'ibCartons',
  'Offline_Mods': 'pr', 'Rcv WaterSpider': 'pr', 'Case Rcv WaterSpider': 'pr', 'MHE Support': 'pid',
  'AROS Avery': 'pid', 'Jam Clear': 'pid',
  'Wall Builder': 'fluidDirect', 'Robotics Operator': 'fluidOffline', 'Transfer Out Packing': 'fluidOffline', 'Fluid Load - Case': 'fluidOffline',
  'Jackpot Kickout': 'aros', 'Robotic Problem Solv': 'aros', 'Robotic Water Spider': 'aros',
  'Transfer Out Kaizen': 'eol', 'Water Spider': 'eol',
  'TO Lead/PA': 'daCartons', 'TO Problem Solve': 'daCartons', 'Transfer Out Ambssdr': 'daCartons', 'TransferOut Training': 'daCartons',
  'Dock Pallet Loader': 'pr', 'TransferOut DockCrew': 'daCartons', 'IXD Pallet Loader': 'pr',
  'Tote Replenishment': 'eol', 'Ship Clerk': 'daCartons', 'Outbound Dock Crew': 'daCartons',
  'Pallet Movement': 'pr', 'Truck Loader': 'daCartons',
  'General Audits': 'ibCartons',
};

// IDRT Sort: EOL Sort + AROS Sort both auto-track to the IDRT function (RSR Support) and
// are no longer separable. Rated on EOL + AROS cartons combined, same as Hourly / Dept Wash.
// Hours = the rsrSupport PPR line (IDRT, from ~Sep 2026) + the pre-IDRT sort codes, so weeks
// before the cutover compare like-for-like: Transfer Out 'Offline_Mods' (old EOL Sort) and
// 'Jackpot Kickout' (old AROS Sort). Receive Support 'Offline_Mods' is Pallet Build, not sort.
const IDRT_SORT = '_idrtSort';
const idrtHours = (w) => (w.lines?.rsrSupport?.hours || 0)
  + (w.functions?.transferOut || []).filter(f => f.name === 'Offline_Mods' || f.name === 'Jackpot Kickout').reduce((s, f) => s + (f.hours || 0), 0);
const idrtCartons = (w) => (w.cartons?.eol || 0) + (w.cartons?.aros || 0);

// Hours for a function in a week. With a parent, only that parent group counts (needed when
// the same FCLM function name exists under more than one parent); otherwise first match (legacy).
const fnHours = (w, fnName, parent) => {
  if (parent) return (w.functions?.[parent] || []).filter(f => f.name === fnName).reduce((s, f) => s + (f.hours || 0), 0);
  const fn = Object.values(w.functions || {}).flat().find(f => f.name === fnName);
  return fn ? (fn.hours || 0) : 0;
};

const PARENT_NAMES = { receiveDock: 'Receive Dock', receiveSupport: 'Receive Support', transferOut: 'Transfer Out', transferOutDock: 'Transfer Out Dock' };

const DEPARTMENTS = {
  pid: {
    name: 'PID',
    functions: [
      { casual: 'PID Unload CPLH', fn: 'Vendor Line Load' },
      { casual: 'PID Divert CPLH', fn: 'PID Manual Divert' },
      { casual: 'PID Inject', fn: 'Receive Line Loader' },
      { casual: 'PID Operator', fn: 'Receive Dock Crew' },
    ],
  },
  aros: {
    name: 'AROS',
    functions: [
      { casual: 'AROS Unload', fn: 'ReceiveLine Unloader' },
      { casual: 'AROS Operator', fn: 'Receive Dock Crew' },
      { casual: 'AROS CSX Label', fn: 'PID Sort Line' },
      { casual: 'AROS Avery', fn: 'AROS Avery' },
      { casual: 'AROS Divert', fn: 'Robotic Problem Solv' },
      // IDRT: AROS Sort shares the IDRT clock with EOL Sort, so it shows the IDRT Sort rate.
      // Hours/volume live on the EOL Sort row only (rateOnly) so totals don't double count.
      { casual: 'AROS Sort', fn: IDRT_SORT, rateOnly: true },
      { casual: 'AROS Waterspider', fn: 'Robotic Water Spider' },
      { casual: 'Re-inject', fn: 'Trans Out Overflow' },
    ],
  },
  ibPR: {
    name: 'IB PR',
    functions: [
      { casual: 'PR Unload CPLH', fn: 'Vendor Sort' },
      // Offline_Mods also exists under Transfer Out (old EOL Sort leftovers); Pallet Build is the Receive Support one.
      { casual: 'Pallet Build CPLH', fn: 'Offline_Mods', parent: 'receiveSupport' },
      { casual: 'Pallet Wrap/Lantech CPLH', fn: 'IB Robotics Operator' },
      { casual: 'Pallet Receive CPLH', fn: 'Direct' },
      { casual: 'PR Waterspider', fn: 'Rcv WaterSpider' },
      { casual: 'IB Pallet EPJ Waterspider', fn: 'Case Rcv WaterSpider' },
      { casual: 'Pallet Auditors', fn: 'General Audits' },
      { casual: 'EPJ Spotter', fn: 'PID Gatekeep' },
    ],
  },
  ibPIT: {
    name: 'IB PIT',
    functions: [
      { casual: 'PIT Pallet Build', fn: 'ReceiveParcel Unload' },
      { casual: 'IB PIT Waterspider', fn: 'Vendor Cutter' },
      { casual: 'PIT Unloader', fn: 'PID Truck Unload' },
    ],
  },
  sorterEOL: {
    name: 'Sorter EOL',
    functions: [
      { casual: 'EOL Avery', fn: 'Tote Replenishment' },
      { casual: 'EOL Divert', fn: 'Transfer Out Kaizen' },
      { casual: 'EOL Sort', fn: IDRT_SORT },
      { casual: 'EOL Waterspider', fn: 'Water Spider' },
      { casual: 'Re-inject', fn: 'Trans Out Overflow' },
    ],
  },
  fluid: {
    name: 'Fluid',
    functions: [
      { casual: 'Fluid WS', fn: 'Transfer Out Packing' },
      { casual: 'Fluid Load', fn: 'Fluid Load - Case' },
      { casual: 'Sorter Wallbuilder', fn: 'Wall Builder' },
      { casual: 'Jam Clear', fn: 'Jam Clear' },
      { casual: 'Wall Builder', fn: 'Robotics Operator' },
    ],
  },
  obPR: {
    name: 'OBPR',
    functions: [
      { casual: 'OB EPJ WaterSpider', fn: 'Outbound Dock Crew' },
      { casual: 'Pallet Loader CPLH', fn: 'Dock Pallet Loader' },
      { casual: 'OB PIT OPERATOR', fn: 'Truck Loader' },
      { casual: 'OB EPJ Operator CPLH', fn: 'IXD Pallet Loader' },
    ],
  },
  ibSupport: {
    name: 'IB Support',
    functions: [
      { casual: 'IB PSolver', fn: 'Receive ProblemSolve' },
      { casual: 'IB Piles/Multi-ASIN Pallet Receive', fn: 'Stow Psolve Backlog' },
      { casual: 'ISS', fn: 'ISS Field Rep' },
      { casual: 'IB TDR', fn: 'Receive Dock TDR' },
      { casual: 'IB/FLOW PA', fn: 'Receive Lead/PA' },
      { casual: 'PR PA', fn: 'Receive Lead/PA' },
      { casual: 'IB Training', fn: 'Receive Training' },
      { casual: 'IB Ambassador', fn: 'Receive Ambassador' },
      { casual: 'IB Clerk', fn: 'Inbound Dock Clerk' },
      { casual: 'IB Lead/PA', fn: '_lineItem_ibLeadPa', isLineItem: true },
      { casual: 'IB Problem Solve', fn: '_lineItem_ibProblemSolve', isLineItem: true },
    ],
  },
  obSupport: {
    name: 'OB Support',
    functions: [
      { casual: 'Fluid Load Sweeper', fn: 'Fluid Load Sweep' },
      { casual: 'OB PS', fn: 'TransferOut PSolve' },
      { casual: 'OB Training', fn: 'TransferOut Training' },
      { casual: 'OB Ambassador', fn: 'Transfer Out Ambssdr' },
      { casual: 'Ship Clerk', fn: 'Ship Clerk' },
      { casual: 'OB TDR', fn: 'TransferOut DockCrew' },
      { casual: 'OB PA', fn: 'Transfer Out Lead/PA' },
      { casual: 'OB Lead/PA', fn: '_lineItem_toLeadPa', isLineItem: true },
      { casual: 'OB Problem Solve', fn: '_lineItem_toProblemSolve', isLineItem: true },
    ],
  },
};

function getWeekNum(weekStart) {
  const d = new Date(weekStart + 'T12:00:00');
  const jan1 = new Date(d.getFullYear(), 0, 1);
  return Math.ceil((((d - jan1) / 86400000) + jan1.getDay() + 1) / 7);
}

export default function Weesa({ api, site }) {
  const [weeklyData, setWeeklyData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [period1Start, setPeriod1Start] = useState('');
  const [period1End, setPeriod1End] = useState('');
  const [period2Start, setPeriod2Start] = useState('');
  const [period2End, setPeriod2End] = useState('');

  useEffect(() => {
    setLoading(true);
    fetch(`${api}/api/meesa?site=${site}`)
      .then(r => r.json())
      .then(data => {
        if (!Array.isArray(data)) { setWeeklyData([]); setLoading(false); return; }
        setWeeklyData(data);
        // Default: Period 1 = HWM week, Period 2 = last complete week
        const filtered = data.filter(w => w.weekStart >= '2026-01-01');
        // Find HWM (highest throughput CPLH)
        let hwm = null, hwmRate = 0;
        const complete = filtered.length > 1 ? filtered.slice(0, -1) : [];
        complete.forEach(w => {
          const c = (w.cartons?.ibCartons || 0) + (w.cartons?.daCartons || 0);
          const h = (w.lines?.totalInbound?.hours || 0) + (w.lines?.daBldgTotal?.hours || 0);
          const rate = h > 0 ? c / h : 0;
          if (rate > hwmRate) { hwmRate = rate; hwm = w; }
        });
        const lastComplete = complete.length > 0 ? complete[complete.length - 1] : null;
        if (hwm) { setPeriod1Start(hwm.weekStart); setPeriod1End(hwm.weekStart); }
        if (lastComplete) { setPeriod2Start(lastComplete.weekStart); setPeriod2End(lastComplete.weekStart); }
        setLoading(false);
      })
      .catch(() => { setWeeklyData([]); setLoading(false); });
  }, [api, site]);

  // Get weeks for a period
  const getWeeksInRange = (start, end) => weeklyData.filter(w => w.weekStart >= start && w.weekStart <= end);

  // Calculate average rate for a function across a range of weeks
  const getAvgRate = (weeks, fnName, isLineItem, parent) => {
    if (fnName === IDRT_SORT) {
      let c = 0, h = 0;
      weeks.forEach(w => { const wh = idrtHours(w); if (wh > 0) { h += wh; c += idrtCartons(w); } });
      return h > 0 && c > 0 ? c / h : 0;
    }
    if (isLineItem) {
      const lineId = fnName.replace('_lineItem_', '');
      let totalVol = 0, totalHours = 0;
      weeks.forEach(w => {
        const line = w.lines?.[lineId];
        if (line && line.hours > 0) {
          totalVol += line.volume || (line.rate * line.hours) || 0;
          totalHours += line.hours;
        }
      });
      return totalHours > 0 ? totalVol / totalHours : 0;
    }
    let totalCartons = 0, totalHours = 0;
    const cartonKey = FN_CARTON_MAP[fnName];
    weeks.forEach(w => {
      const h = fnHours(w, fnName, parent);
      if (!h) return;
      if (cartonKey && w.cartons) {
        totalCartons += w.cartons[cartonKey] || 0;
      }
      totalHours += h;
    });
    return totalHours > 0 && totalCartons > 0 ? totalCartons / totalHours : 0;
  };

  // Get total hours for a function across weeks
  const getTotalHours = (weeks, fnName, isLineItem, rateOnly, parent) => {
    if (rateOnly) return 0;
    if (fnName === IDRT_SORT) return weeks.reduce((sum, w) => sum + idrtHours(w), 0);
    if (isLineItem) {
      const lineId = fnName.replace('_lineItem_', '');
      return weeks.reduce((sum, w) => sum + (w.lines?.[lineId]?.hours || 0), 0);
    }
    return weeks.reduce((sum, w) => sum + fnHours(w, fnName, parent), 0);
  };

  // Get total volume for a function across weeks
  const getTotalVolume = (weeks, fnName, isLineItem, rateOnly) => {
    if (rateOnly) return 0;
    if (fnName === IDRT_SORT) return weeks.reduce((sum, w) => sum + (idrtHours(w) > 0 ? idrtCartons(w) : 0), 0);
    if (isLineItem) {
      const lineId = fnName.replace('_lineItem_', '');
      return weeks.reduce((sum, w) => {
        const line = w.lines?.[lineId];
        return sum + (line ? (line.volume || (line.rate * line.hours) || 0) : 0);
      }, 0);
    }
    const cartonKey = FN_CARTON_MAP[fnName];
    if (!cartonKey) return 0;
    return weeks.reduce((sum, w) => sum + (w.cartons?.[cartonKey] || 0), 0);
  };

  const period1Weeks = getWeeksInRange(period1Start, period1End);
  const period2Weeks = getWeeksInRange(period2Start, period2End);

  // Available weeks for dropdowns
  const availableWeeks = weeklyData.filter(w => w.weekStart >= '2025-12-28').map(w => w.weekStart);

  const cellStyle = { padding: '4px 8px', textAlign: 'center', fontSize: '0.7rem', fontWeight: 500, border: '1px solid #000', whiteSpace: 'nowrap' };
  const headerStyle = { ...cellStyle, fontWeight: 700, background: '#232f3e', color: '#fff' };
  const labelStyle = { ...cellStyle, textAlign: 'left', fontWeight: 600, background: '#fff' };
  const groupStyle = { ...cellStyle, textAlign: 'left', fontWeight: 700, background: '#e3f2fd', color: '#1565c0', fontSize: '0.8rem', padding: '8px' };

  const p1Label = period1Start === period1End ? `Wk ${getWeekNum(period1Start)}` : `Wk ${getWeekNum(period1Start)}-${getWeekNum(period1End)}`;
  const p2Label = period2Start === period2End ? `Wk ${getWeekNum(period2Start)}` : `Wk ${getWeekNum(period2Start)}-${getWeekNum(period2End)}`;

  return (
    <div>
      <h2 className="section-title">HWM Comparison — {site}</h2>
      <div className="card">
                <div className="form-row" style={{ flexWrap: 'wrap' }}>
          <div className="form-group">
            <label>Period 1 (Baseline)</label>
            <div style={{ display: 'flex', gap: 4 }}>
              <select value={period1Start} onChange={e => setPeriod1Start(e.target.value)} style={{ fontSize: '0.75rem' }}>
                {availableWeeks.map(w => <option key={w} value={w}>Wk {getWeekNum(w)} ({w})</option>)}
              </select>
              <span style={{ alignSelf: 'center', fontSize: '0.7rem' }}>to</span>
              <select value={period1End} onChange={e => setPeriod1End(e.target.value)} style={{ fontSize: '0.75rem' }}>
                {availableWeeks.map(w => <option key={w} value={w}>Wk {getWeekNum(w)} ({w})</option>)}
              </select>
            </div>
          </div>
          <div className="form-group">
            <label>Period 2 (Compare)</label>
            <div style={{ display: 'flex', gap: 4 }}>
              <select value={period2Start} onChange={e => setPeriod2Start(e.target.value)} style={{ fontSize: '0.75rem' }}>
                {availableWeeks.map(w => <option key={w} value={w}>Wk {getWeekNum(w)} ({w})</option>)}
              </select>
              <span style={{ alignSelf: 'center', fontSize: '0.7rem' }}>to</span>
              <select value={period2End} onChange={e => setPeriod2End(e.target.value)} style={{ fontSize: '0.75rem' }}>
                {availableWeeks.map(w => <option key={w} value={w}>Wk {getWeekNum(w)} ({w})</option>)}
              </select>
            </div>
          </div>
        </div>
        <p style={{ fontSize: '0.7rem', color: '#666' }}>
          Period 1: {period1Weeks.length} week(s) | Period 2: {period2Weeks.length} week(s) | Hours +/- shows impact of rate change on Period 2 volume
        </p>
      </div>

      {!loading && period1Weeks.length > 0 && period2Weeks.length > 0 && (
        <div className="card" style={{ overflowX: 'auto' }}>
          <table style={{ borderCollapse: 'collapse', width: '100%' }}>
            <thead>
              <tr>
                <th style={{ ...headerStyle, textAlign: 'left', minWidth: 180 }}>Function</th>
                <th style={headerStyle}>{p1Label} Rate</th>
                <th style={headerStyle}>{p2Label} Rate</th>
                <th style={headerStyle}>% Diff</th>
                <th style={headerStyle}>{p2Label} Hours</th>
                <th style={headerStyle}>Hours +/-</th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(DEPARTMENTS).map(([deptKey, dept]) => {
                let groupHoursSpent = 0, groupHoursDelta = 0;

                const rows = dept.functions.map(({ casual, fn: fnName, isLineItem, rateOnly, parent }, idx) => {
                  const rate1 = getAvgRate(period1Weeks, fnName, isLineItem, parent);
                  const rate2 = getAvgRate(period2Weeks, fnName, isLineItem, parent);
                  const hours2 = getTotalHours(period2Weeks, fnName, isLineItem, rateOnly, parent);
                  const volume2 = getTotalVolume(period2Weeks, fnName, isLineItem, rateOnly);
                  const pctDiff = rate1 > 0 ? ((rate2 - rate1) / rate1) * 100 : 0;
                  const hoursAtRate1 = rate1 > 0 ? volume2 / rate1 : 0;
                  const hoursDelta = hoursAtRate1 > 0 ? hoursAtRate1 - hours2 : 0;

                  groupHoursSpent += hours2;
                  groupHoursDelta += hoursDelta;

                  if (hours2 === 0 && rate1 === 0 && rate2 === 0) return null;

                  const diffColor = pctDiff > 0 ? '#4caf50' : pctDiff < 0 ? '#ef5350' : '#888';
                  const deltaColor = hoursDelta > 0 ? '#4caf50' : hoursDelta < 0 ? '#ef5350' : '#888';

                  return (
                    <tr key={`${deptKey}-${idx}`}>
                      <td style={{ ...cellStyle, textAlign: 'left', color: '#333', fontWeight: 600 }}>{casual}</td>
                      <td style={{ ...cellStyle, color: '#e65100' }}>{rate1 ? rate1.toFixed(1) : '—'}</td>
                      <td style={{ ...cellStyle, color: '#1565c0' }}>{rate2 ? rate2.toFixed(1) : '—'}</td>
                      <td style={{ ...cellStyle, color: diffColor, fontWeight: 600 }}>{pctDiff ? `${pctDiff > 0 ? '+' : ''}${pctDiff.toFixed(0)}%` : '—'}</td>
                      <td style={{ ...cellStyle, color: '#333' }}>{hours2 ? Math.round(hours2).toLocaleString() : '—'}</td>
                      <td style={{ ...cellStyle, color: deltaColor, fontWeight: 700 }}>{hoursDelta ? `${hoursDelta > 0 ? '+' : ''}${Math.round(hoursDelta).toLocaleString()}` : '—'}</td>
                    </tr>
                  );
                }).filter(Boolean);

                const groupDeltaColor = groupHoursDelta > 0 ? '#4caf50' : groupHoursDelta < 0 ? '#ef5350' : '#888';

                return (
                  <React.Fragment key={deptKey}>
                    <tr><td colSpan={6} style={groupStyle}>{dept.name}</td></tr>
                    {rows}
                    <tr style={{ borderTop: '2px solid #555' }}>
                      <td style={{ ...cellStyle, textAlign: 'left', fontWeight: 700, color: '#1565c0' }}>{dept.name} Total</td>
                      <td style={cellStyle}></td>
                      <td style={cellStyle}></td>
                      <td style={cellStyle}></td>
                      <td style={{ ...cellStyle, color: '#333', fontWeight: 700 }}>{groupHoursSpent ? Math.round(groupHoursSpent).toLocaleString() : '—'}</td>
                      <td style={{ ...cellStyle, color: groupDeltaColor, fontWeight: 700 }}>{groupHoursDelta ? `${groupHoursDelta > 0 ? '+' : ''}${Math.round(groupHoursDelta).toLocaleString()}` : '—'}</td>
                    </tr>
                  </React.Fragment>
                );
              })}
              {/* Grand Total */}
              {(() => {
                let totalHours = 0, totalDelta = 0;
                Object.values(DEPARTMENTS).forEach(dept => {
                  dept.functions.forEach(({ fn: fnName, isLineItem, rateOnly, parent }) => {
                    const rate1 = getAvgRate(period1Weeks, fnName, isLineItem, parent);
                    const hours2 = getTotalHours(period2Weeks, fnName, isLineItem, rateOnly, parent);
                    const volume2 = getTotalVolume(period2Weeks, fnName, isLineItem, rateOnly);
                    const hoursAtRate1 = rate1 > 0 ? volume2 / rate1 : 0;
                    totalHours += hours2;
                    totalDelta += hoursAtRate1 > 0 ? hoursAtRate1 - hours2 : 0;
                  });
                });
                const color = totalDelta > 0 ? '#4caf50' : totalDelta < 0 ? '#ef5350' : '#888';
                return (
                  <tr style={{ borderTop: '3px solid #4fc3f7' }}>
                    <td style={{ ...cellStyle, textAlign: 'left', fontWeight: 700, color: '#1565c0', fontSize: '0.85rem' }}>GRAND TOTAL</td>
                    <td style={cellStyle}></td>
                    <td style={cellStyle}></td>
                    <td style={cellStyle}></td>
                    <td style={{ ...cellStyle, color: '#333', fontWeight: 700, fontSize: '0.85rem' }}>{totalHours ? Math.round(totalHours).toLocaleString() : '—'}</td>
                    <td style={{ ...cellStyle, color, fontWeight: 700, fontSize: '0.85rem' }}>{totalDelta ? `${totalDelta > 0 ? '+' : ''}${Math.round(totalDelta).toLocaleString()}` : '—'}</td>
                  </tr>
                );
              })()}
            </tbody>
          </table>
        </div>
      )}

      {/* Volume Summary Table */}
      {!loading && period1Weeks.length > 0 && period2Weeks.length > 0 && (() => {
        const getVol = (weeks, key) => weeks.reduce((s, w) => s + (w.cartons?.[key] || 0), 0);
        const p1 = {
          pid: getVol(period1Weeks, 'pid'),
          aros: getVol(period1Weeks, 'aros'),
          pr: getVol(period1Weeks, 'pr'),
          ib: getVol(period1Weeks, 'ibCartons'),
          da: getVol(period1Weeks, 'daCartons'),
          fluidDirect: getVol(period1Weeks, 'fluidDirect'),
          fluidOffline: getVol(period1Weeks, 'fluidOffline'),
          eol: getVol(period1Weeks, 'eol'),
          pit: getVol(period1Weeks, 'pit'),
        };
        const p2 = {
          pid: getVol(period2Weeks, 'pid'),
          aros: getVol(period2Weeks, 'aros'),
          pr: getVol(period2Weeks, 'pr'),
          ib: getVol(period2Weeks, 'ibCartons'),
          da: getVol(period2Weeks, 'daCartons'),
          fluidDirect: getVol(period2Weeks, 'fluidDirect'),
          fluidOffline: getVol(period2Weeks, 'fluidOffline'),
          eol: getVol(period2Weeks, 'eol'),
          pit: getVol(period2Weeks, 'pit'),
        };
        // Use stored carton splits (real actuals). Only fall back to the ~33%
        // EOL estimate for a period that has no stored fluid data at all.
        if (!p1.fluidDirect && !p1.fluidOffline && !p1.eol) {
          p1.eol = Math.round(p1.pid * 0.33);
          p1.fluidDirect = p1.pid - p1.eol;
          p1.fluidOffline = p1.eol + p1.aros;
        }
        if (!p2.fluidDirect && !p2.fluidOffline && !p2.eol) {
          p2.eol = Math.round(p2.pid * 0.33);
          p2.fluidDirect = p2.pid - p2.eol;
          p2.fluidOffline = p2.eol + p2.aros;
        }
        // PIT falls back to PR (IB pallet) when daPR/pit isn't stored separately.
        if (!p1.pit) p1.pit = p1.pr;
        if (!p2.pit) p2.pit = p2.pr;

        // FPP% = process path volume / total inbound
        const fpp = (vol, total) => total > 0 ? ((vol / total) * 100).toFixed(1) + '%' : '—';

        const volRows = [
          { label: 'PID', v1: p1.pid, v2: p2.pid },
          { label: 'AROS', v1: p1.aros, v2: p2.aros },
          { label: 'PR / Pallet', v1: p1.pr, v2: p2.pr },
          { label: 'Fluid Direct', v1: p1.fluidDirect, v2: p2.fluidDirect },
          { label: 'Fluid Offline', v1: p1.fluidOffline, v2: p2.fluidOffline },
          { label: 'EOL', v1: p1.eol, v2: p2.eol },
          { label: 'PIT', v1: p1.pit, v2: p2.pit },
          { label: 'Total Inbound', v1: p1.ib, v2: p2.ib, bold: true },
          { label: 'Total Outbound', v1: p1.da, v2: p2.da, bold: true },
        ];
        const fppRows = [
          { label: 'PID FPP%', v1: fpp(p1.pid, p1.ib), v2: fpp(p2.pid, p2.ib) },
          { label: 'AROS FPP%', v1: fpp(p1.aros, p1.ib), v2: fpp(p2.aros, p2.ib) },
          { label: 'PR FPP%', v1: fpp(p1.pr, p1.ib), v2: fpp(p2.pr, p2.ib) },
          { label: 'EOL FPP%', v1: fpp(p1.eol, p1.ib), v2: fpp(p2.eol, p2.ib) },
        ];

        return (
          <div className="card" style={{ overflowX: 'auto', marginTop: 16 }}>
            <h3 className="section-title">Volume by Process Path</h3>
            <table style={{ borderCollapse: 'collapse', width: 'auto' }}>
              <thead>
                <tr>
                  <th style={{ ...headerStyle, textAlign: 'left', minWidth: 140 }}>Carton Type</th>
                  <th style={headerStyle}>{p1Label}</th>
                  <th style={headerStyle}>{p2Label}</th>
                </tr>
              </thead>
              <tbody>
                {volRows.map(row => (
                  <tr key={row.label}>
                    <td style={{ ...cellStyle, textAlign: 'left', fontWeight: row.bold ? 700 : 500 }}>{row.label}</td>
                    <td style={{ ...cellStyle, fontWeight: row.bold ? 700 : 500 }}>{row.v1 ? Math.round(row.v1).toLocaleString() : '—'}</td>
                    <td style={{ ...cellStyle, fontWeight: row.bold ? 700 : 500 }}>{row.v2 ? Math.round(row.v2).toLocaleString() : '—'}</td>
                  </tr>
                ))}
                <tr><td colSpan={3} style={{ ...cellStyle, background: '#e3f2fd', fontWeight: 700, textAlign: 'left', color: '#1565c0' }}>FPP %</td></tr>
                {fppRows.map(row => (
                  <tr key={row.label}>
                    <td style={{ ...cellStyle, textAlign: 'left', fontStyle: 'italic' }}>{row.label}</td>
                    <td style={cellStyle}>{row.v1}</td>
                    <td style={cellStyle}>{row.v2}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );
      })()}
    </div>
  );
}
